// Боевая механика: расчёт урона, ответный удар, проверка возможности
// атаковать Героя (Авангард Героя).
//
// pierce (Пробитие): применяется отдельно к КАЖДОЙ исходящей атаке.
// Ответный удар цели имеет pierce только если у цели свой pierce keyword
// (то есть не наследуется от атакующего).

import { hasKeyword, KW } from './keywords.js';
import { absorbDamageIfImmune } from './keywords/immune.js';
import { fireOnAttackTrigger } from './abilities/livlife/on-attack-triggers.js';
import { fireOnDamageTakenTrigger } from './abilities/livlife/on-damage-triggers.js';

// Можно ли атаковать Героя противника?
// Авангард считают только ВИДИМЫЕ юниты — стелс не защищает Героя.
// А.4.4.4: Спящий агент НЕ считается для Авангарда (это «карта-бомба»).
// Если все живые юниты противника стелс / спящие — Герой уязвим.
export function canAttackHero(enemyPlayer) {
  const visible = enemyPlayer.unitsOnField()
    .filter(u => !hasKeyword(u, KW.STEALTH))
    .filter(u => u.id !== 'deus_inf_spyashy_agent');
  return visible.length === 0;
}

// Применить dmg к target. opts.pierce — если true, броня игнорируется.
// opts.state, opts.source — опционально, для логирования и поглощения Иммунитетом.
// Возвращает { armorAbsorbed, hpLost, ignoredArmor, immuneAbsorbed }.
export function applyDamage(target, dmg, opts = {}) {
  if (dmg <= 0) return { armorAbsorbed: 0, hpLost: 0, ignoredArmor: 0, immuneAbsorbed: 0 };

  // В.2.d-1: Иммунитет ловит весь урон ДО Брони и Пробития. После
  // поглощения — Иммунитет снимается (см. immune.js).
  if (absorbDamageIfImmune(opts.state, target, dmg, opts.source)) {
    return { armorAbsorbed: 0, hpLost: 0, ignoredArmor: 0, immuneAbsorbed: dmg };
  }

  if (opts.pierce) {
    // Броня игнорируется. Записываем «сколько брони было проигнорировано» —
    // нужно для понятного лога: "(пробитие, броня N игнор.)".
    const ignored = Math.min(target.armor, dmg);
    target.hp -= dmg;
    // В.2.e-2: триггер «получил урон» — для Подопытного Альфы и т.п.
    // Этап 56.55: прокидываем opts.source (атакующий) — нужен Подмазанному стукачу.
    if (dmg > 0) fireOnDamageTakenTrigger(opts.state, target, dmg, 0, opts.source);
    return { armorAbsorbed: 0, hpLost: dmg, ignoredArmor: ignored, immuneAbsorbed: 0 };
  }

  let remaining = dmg;
  let absorbed = 0;
  if (target.armor > 0) {
    absorbed = Math.min(target.armor, remaining);
    target.armor -= absorbed;
    remaining -= absorbed;
  }
  if (remaining > 0) target.hp -= remaining;
  // В.2.e-2: триггер «получил урон» — за каждый потерянный hp.
  // Этап 56.55: прокидываем opts.source (атакующий) — нужен Подмазанному стукачу.
  if (remaining > 0) fireOnDamageTakenTrigger(opts.state, target, remaining, absorbed, opts.source);
  return { armorAbsorbed: absorbed, hpLost: remaining, ignoredArmor: 0, immuneAbsorbed: 0 };
}

// Снимок HP/брони цели — для лога "(HP X→Y, броня A→B)".
function snapshot(t) {
  return { hp: t.hp, armor: t.armor };
}

// Атака юнит-vs-юнит с одновременным ответным ударом.
// pierce учитывается отдельно для каждой стороны.
// currentTurn — нужен, чтобы проверить Режим ожидания у defender:
// спящий не отвечает (диздок 6.6).
// opts.noCounter — true для Инфильтратора на 2-й линии (нет ответного удара).
export function unitVsUnit(attacker, defender, currentTurn, opts = {}) {
  const aPierce = hasKeyword(attacker, KW.PIERCE);
  const dPierce = hasKeyword(defender, KW.PIERCE);
  const defenderSleeps = currentTurn !== undefined && defender.isSleeping(currentTurn);
  const defenderBefore = snapshot(defender);
  const attackerBefore = snapshot(attacker);
  const fwd = applyDamage(defender, attacker.atk, { pierce: aPierce, state: opts.state, source: attacker });
  // В.2.e-1: on_attack триггер атакующего после применения урона по цели.
  // damageDealt = броня + hp, БЕЗ immune (immune = «удар не состоялся»).
  if (attacker.atk > 0) {
    fireOnAttackTrigger(opts.state, attacker, defender, (fwd.armorAbsorbed || 0) + (fwd.hpLost || 0));
  }
  const counterSuppressed = !!opts.noCounter;
  // Б.2.c: defender.cantAttack (ИИ-ядро, Подпольный боец без Модов) — нет контратаки.
  // C.1 Этап 33: Спящий агент в planted/armed состоянии «спит» как карта-бомба
  // (диздок 7.1). Он может получить урон от не-same-faction юнита, но НЕ
  // контратакует и не участвует в обычной фазе боя. До фикса контратаковал
  // на 3 урона, как обычный юнит.
  const spyDormant = defender.sleeperState === 'planted' || defender.sleeperState === 'armed';
  const defDmg = (defenderSleeps || counterSuppressed || defender.cantAttack || spyDormant) ? 0 : defender.atk;
  const bwd = applyDamage(attacker, defDmg, { pierce: dPierce, state: opts.state, source: defender });
  // В.2.e-1: on_attack триггер контратакующего (defender нанёс удар по
  // attacker'у).
  if (defDmg > 0) {
    fireOnAttackTrigger(opts.state, defender, attacker, (bwd.armorAbsorbed || 0) + (bwd.hpLost || 0));
  }
  const defenderAfter = snapshot(defender);
  const attackerAfter = snapshot(attacker);
  return {
    attacker, defender,
    atkDmg: attacker.atk, defDmg,
    atkPierce: aPierce, defPierce: dPierce,
    fwd, bwd,
    defenderSleeps,
    counterSuppressed,
    defenderBefore, defenderAfter,
    attackerBefore, attackerAfter
  };
}

// Юнит атакует Героя — без ответного удара (правило А.1).
// opts.state — для логирования Иммунитета.
export function unitVsHero(attacker, hero, opts = {}) {
  const pierce = hasKeyword(attacker, KW.PIERCE);
  const targetBefore = snapshot(hero);
  const fwd = applyDamage(hero, attacker.atk, { pierce, state: opts.state, source: attacker });
  // В.2.e-1: on_attack триггер атакующего юнита по Герою.
  if (attacker.atk > 0) {
    fireOnAttackTrigger(opts.state, attacker, hero, (fwd.armorAbsorbed || 0) + (fwd.hpLost || 0));
  }
  const targetAfter = snapshot(hero);
  return {
    attacker, hero,
    atkDmg: attacker.atk,
    atkPierce: pierce,
    fwd,
    targetBefore, targetAfter
  };
}

// Этап 21: Герой атакует юнита — С ОТВЕТНЫМ УДАРОМ.
// Защищающийся юнит отвечает Герою своей атакой (как в unit-vs-unit).
// Спящий defender не отвечает (Initiation Cooldown). defender.cantAttack — тоже.
// pierce учитывается отдельно для каждой стороны.
//
// currentTurn — для проверки спящего; передаётся в opts.currentTurn.
export function heroVsUnit(hero, defender, opts = {}) {
  const aPierce = hasKeyword(hero, KW.PIERCE);
  const dPierce = hasKeyword(defender, KW.PIERCE);
  const defenderSleeps = opts.currentTurn !== undefined && defender.isSleeping?.(opts.currentTurn);
  const targetBefore = snapshot(defender);
  const attackerBefore = snapshot(hero);
  const fwd = applyDamage(defender, hero.atk, { pierce: aPierce, state: opts.state, source: hero });
  // Контратака.
  const counterSuppressed = !!opts.noCounter;
  const defDmg = (defenderSleeps || counterSuppressed || defender.cantAttack) ? 0 : defender.atk;
  const bwd = applyDamage(hero, defDmg, { pierce: dPierce, state: opts.state, source: defender });
  // В.2.e-1: on_attack триггер контратакующего (defender нанёс удар по Герою).
  if (defDmg > 0) {
    fireOnAttackTrigger(opts.state, defender, hero, (bwd.armorAbsorbed || 0) + (bwd.hpLost || 0));
  }
  const targetAfter = snapshot(defender);
  const attackerAfter = snapshot(hero);
  return {
    hero, defender,
    atkDmg: hero.atk, defDmg,
    atkPierce: aPierce, defPierce: dPierce,
    fwd, bwd,
    defenderSleeps,
    counterSuppressed,
    targetBefore, targetAfter,
    attackerBefore, attackerAfter
  };
}

// Этап 21: Герой атакует Героя — С ОТВЕТНЫМ УДАРОМ.
// Защищающийся Герой отвечает атакующему своей атакой.
// pierce учитывается отдельно для каждой стороны.
export function heroVsHero(hero, enemyHero, opts = {}) {
  const aPierce = hasKeyword(hero, KW.PIERCE);
  const dPierce = hasKeyword(enemyHero, KW.PIERCE);
  const targetBefore = snapshot(enemyHero);
  const attackerBefore = snapshot(hero);
  const fwd = applyDamage(enemyHero, hero.atk, { pierce: aPierce, state: opts.state, source: hero });
  // Контратака защищающегося Героя.
  const bwd = applyDamage(hero, enemyHero.atk, { pierce: dPierce, state: opts.state, source: enemyHero });
  const targetAfter = snapshot(enemyHero);
  const attackerAfter = snapshot(hero);
  return {
    hero, enemyHero,
    atkDmg: hero.atk, defDmg: enemyHero.atk,
    atkPierce: aPierce, defPierce: dPierce,
    fwd, bwd,
    targetBefore, targetAfter,
    attackerBefore, attackerAfter
  };
}
